/**
 * Course-card variant registry. Maps a variant id to its component.
 *
 * <p>{@code 'custom'} is the theme-override slot — hosts pass a
 * registered theme primitive in. With no override, {@code 'custom'}
 * falls back to {@code DefaultCourseCard}.
 */
import type { ComponentType } from 'react';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type { ButtonRadius as UiButtonRadius } from '@akademi/ui';

import { DefaultCourseCard } from './DefaultCourseCard';
import { CompactCourseCard } from './CompactCourseCard';
import { WideCourseCard } from './WideCourseCard';
import { MinimalCourseCard } from './MinimalCourseCard';
import { CinematicCourseCard } from './CinematicCourseCard';
import { MarketplaceCourseCard } from './MarketplaceCourseCard';
import { AcademicCourseCard } from './AcademicCourseCard';
import { UdemyCourseCard } from './UdemyCourseCard';
import { ClassicHighCourseCard } from './ClassicHighCourseCard';
import { ClassicNetflixZoomCourseCard } from './ClassicNetflixZoomCourseCard';
import { BorderlessCourseCard } from './BorderlessCourseCard';

export type CourseCardComponent = ComponentType<{
  course: CourseCardProps;
  /** When false, variants that render a star rating hide it. Defaults to
   *  shown. Threaded from the block's admin-managed {@code showRating} prop. */
  showRating?: boolean;
  /** CTA button corner-radius preset (admin-managed {@code buttonRadius}).
   *  {@code 'default'}/undefined inherits the theme radius token. */
  buttonRadius?: ButtonRadius;
  /** How the certificate marker renders on cards that show one (currently
   *  the {@code udemy} hover popover). Admin-managed. Defaults to
   *  {@code 'both'}. */
  certificateDisplay?: CertificateDisplay;
  /** Autoplay the course preview clip when the course has a {@code
   *  previewVideoUrl} — inside the {@code udemy} card's hover popover, or
   *  in place of the {@code classicNetflixZoom} card's cover while it's
   *  zoomed. Admin-managed. Defaults to {@code false} — a static poster. */
  autoPlayPreview?: boolean;
  /** Where the {@code udemy} card's hover popover opens relative to the
   *  card. {@code 'left'}/{@code 'right'} pin a side (falling back to the
   *  other when there's no room); {@code 'center'} overlays the popover on
   *  the card itself. Admin-managed. Defaults to {@code 'right'} (falling
   *  back to the left when there's no room). */
  popoverPosition?: PopoverPosition;
  /** When true (default), the {@code udemy} card's hover-popover title
   *  links to the course, same as the card face. Admin-managed. */
  popoverTitleLink?: boolean;
  /** Progress bar shown on the card for courses carrying {@code
   *  course.progress}, positioned per-variant near the card's footer/meta
   *  area. {@code show} (default true) toggles the bar; {@code showText}
   *  (default false) adds the percentage label; {@code textPlace}
   *  (default {@code 'outside-right'}) positions that label beside or on
   *  top of the track. Admin-managed; every card variant reads it. */
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}> & {
  gridSpec?: CardGridSpec;
};

/** Admin-selectable CTA button radius. Aliases {@link UiButtonRadius} so
 *  blocks and {@code @akademi/ui}'s {@code Button} share one preset
 *  vocabulary; the resolution to a CSS value lives in
 *  {@code @akademi/ui}'s {@code resolveButtonRadius}. */
export type ButtonRadius = UiButtonRadius;

/** Admin-selectable certificate display style. Mirrors
 *  {@code CertificateBadgeMode}: badge image only, text pill only, or both. */
export type CertificateDisplay = 'badge' | 'text' | 'both';

/** Admin-selectable placement of the {@code udemy} card's progress-bar
 *  percentage label. {@code 'outside-*'} places it beside the track;
 *  {@code 'inside-*'} overlays it directly on the track (centered, or
 *  pinned to an edge). */
export type ProgressTextPlace =
  | 'outside-right'
  | 'outside-left'
  | 'inside-center'
  | 'inside-left'
  | 'inside-right';

/** Admin-selectable hover-popover placement for the {@code udemy} card.
 *  {@code 'left'}/{@code 'right'} pin a side (falling back to the other when
 *  there's no room); {@code 'center'} overlays the popover on the card. */
export type PopoverPosition = 'left' | 'center' | 'right';

export type CourseCardVariant =
  | 'default'
  | 'compact'
  | 'wide'
  | 'minimal'
  | 'cinematic'
  | 'marketplace'
  | 'academic'
  | 'udemy'
  | 'classicHigh'
  | 'classicNetflixZoom'
  | 'borderless'
  | 'custom';

const REGISTRY: Record<Exclude<CourseCardVariant, 'custom'>, CourseCardComponent> = {
  default: DefaultCourseCard,
  compact: CompactCourseCard,
  wide: WideCourseCard,
  minimal: MinimalCourseCard,
  cinematic: CinematicCourseCard,
  marketplace: MarketplaceCourseCard,
  udemy: UdemyCourseCard,
  academic: AcademicCourseCard,
  classicHigh: ClassicHighCourseCard,
  classicNetflixZoom: ClassicNetflixZoomCourseCard,
  borderless: BorderlessCourseCard,
};

/**
 * Resolve a card-style id to its component. The {@code 'custom'} slot
 * accepts an optional theme primitive — pass it in {@code options}.
 *
 * <p>Theme primitives may be written to take {@code CourseCardProps}
 * DIRECTLY (the simpler signature). The registry expects
 * {@code { course }}, so primitives are wrapped in an adapter that
 * unwraps the {@code course} prop and spreads it.
 */
export function resolveCourseCard(
  cardStyle: string | undefined,
  options?: {
    customPrimitive?: ComponentType<CourseCardProps> | ComponentType<{ course: CourseCardProps }>;
  },
): CourseCardComponent {
  const normalised = cardStyle ?? 'default';

  if (normalised === 'custom') {
    const Custom = options?.customPrimitive;
    if (!Custom) return DefaultCourseCard;
    return makePrimitiveAdapter(Custom) as CourseCardComponent;
  }

  return (REGISTRY as Record<string, CourseCardComponent>)[normalised] ?? DefaultCourseCard;
}

function makePrimitiveAdapter(
  Primitive: ComponentType<CourseCardProps> | ComponentType<{ course: CourseCardProps }>,
): ComponentType<{ course: CourseCardProps }> {
  function Adapter({
    course,
    showRating,
    buttonRadius,
  }: {
    course: CourseCardProps;
    showRating?: boolean;
    buttonRadius?: ButtonRadius;
  }) {
    // Try the wrapped shape first via duck-typing: pass `course` and
    // the primitive picks it up. Most primitives written for this
    // package use { course }; ones written to take props directly
    // still work because spreading a single object matches both
    // signatures from React's perspective.
    const P = Primitive as ComponentType<
      {
        course: CourseCardProps;
        showRating?: boolean;
        buttonRadius?: ButtonRadius;
      } & CourseCardProps
    >;
    return <P {...course} course={course} showRating={showRating} buttonRadius={buttonRadius} />;
  }
  Adapter.displayName = 'CourseCardPrimitiveAdapter';
  return Adapter;
}
